<template>
  <div class="getting-started-checklist w-tw">
    <div class="min-h-[calc(100vh-var(--topbar-height))] bg-gray-50">
      <Welcome
        v-if="!started"
        @start="start"
      />
      <template v-else>
        <header class="sticky top-(--topbar-height) z-20 bg-white px-4 py-5 shadow-sm md:px-16">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <h1 class="text-2xl font-light text-gray-800">
              <span aria-hidden="true">👋</span> {{ t('welcome', { name: currentUser.first_name }) }}
            </h1>
            <div class="flex shrink-0 items-center gap-3">
              <span
                v-if="planName"
                class="whitespace-nowrap rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700"
                data-testid="plan-pill"
              >
                {{ t('your_plan', { plan: planName }) }}
              </span>
              <ZoneButton
                v-if="allCompleted && reviewing"
                color="primary_alt"
                :href="dashboardUrl"
                trailing-icon="arrow_forward"
                data-testid="header-dashboard"
              >
                {{ t('go_to_dashboard') }}
              </ZoneButton>
            </div>
          </div>
        </header>

        <main class="@container px-4 pb-6 md:px-16">
          <Completion
            v-if="allCompleted && !reviewing"
            :dashboard-url="dashboardUrl"
            :user-organization-id="userOrganizationId"
            :organization-name="organization.name"
            :auto-dismiss-on="autoDismissOn"
            :auto-dismiss-days="autoDismissDays"
            :sidebar-dismissed="sidebarDismissed"
            @review="review"
          />
          <div
            v-else
            class="mx-auto mt-6 flex w-full max-w-lg flex-col gap-6"
          >
            <div class="flex flex-col gap-6 @7xl:flex-row">
              <StepList
                :steps="steps"
                :active-id="step"
                :just-done-id="justDoneId"
                @open="open"
              />
              <div class="flex min-w-0 flex-1 flex-col gap-4">
                <component
                  :is="activeStep.component"
                  v-if="activeStep"
                  :key="`${step}-${remounts}`"
                  class="step-lift"
                  v-bind="activeStep.props"
                />
              </div>
            </div>
            <SetupCallBanner
              v-if="setupCallUrl && !setupCallPostponed"
              :setup-call-url
              :organization-id="organization.id"
              @remind-later="setupCallPostponed = true"
            />
          </div>
        </main>

        <ZoneToastStack
          :toasts
          @close="removeToast"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { router } from '@inertiajs/vue3';
import { options as floatingOptions } from 'floating-vue';
import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneButton from 'zone/components/base/Button.vue';
import ZoneToastStack from 'zone/components/base/ToastStack.vue';
import Completion from '~/components/experiments/0239_getting_started_checklist/Completion.vue';
import SetupCallBanner from '~/components/experiments/0239_getting_started_checklist/SetupCallBanner.vue';
import StepList from '~/components/experiments/0239_getting_started_checklist/StepList.vue';
import Welcome from '~/components/experiments/0239_getting_started_checklist/Welcome.vue';
import ConnectPayroll from '~/components/experiments/0239_getting_started_checklist/steps/ConnectPayroll.vue';
import CreateOrganization from '~/components/experiments/0239_getting_started_checklist/steps/CreateOrganization.vue';
import CreateTeams from '~/components/experiments/0239_getting_started_checklist/steps/CreateTeams.vue';
import CustomizeProject from '~/components/experiments/0239_getting_started_checklist/steps/CustomizeProject.vue';
import InviteTeammates from '~/components/experiments/0239_getting_started_checklist/steps/InviteTeammates.vue';
import MemberLimits from '~/components/experiments/0239_getting_started_checklist/steps/MemberLimits.vue';
import PaymentDetails from '~/components/experiments/0239_getting_started_checklist/steps/PaymentDetails.vue';
import SmartNotification from '~/components/experiments/0239_getting_started_checklist/steps/SmartNotification.vue';
import {
  auto_dashboard_path,
  organization_getting_started_index_path,
} from '~/routes';

const STEP_COMPONENTS = {
  create_org: CreateOrganization,
  invite_members: InviteTeammates,
  create_teams: CreateTeams,
  set_up_project: CustomizeProject,
  set_up_time_limits: MemberLimits,
  create_a_smart_notification: SmartNotification,
  set_member_payment_details: PaymentDetails,
  connect_payroll: ConnectPayroll,
};

const {
  steps, step = null, stepData = null, planName = null, currentUser, organization, showWelcome,
  userOrganizationId, autoDismissOn, autoDismissDays, sidebarDismissed, setupCallUrl = null,
} = defineProps({
  steps: { type: Array, required: true },
  step: { type: String },
  stepData: { type: [Object, Array] },
  planName: { type: String },
  currentUser: { type: Object, required: true },
  organization: { type: Object, required: true },
  showWelcome: { type: Boolean },
  userOrganizationId: { type: Number, required: true },
  autoDismissOn: { type: String, required: true },
  autoDismissDays: { type: Number, required: true },
  sidebarDismissed: { type: Boolean },
  setupCallUrl: { type: String },
});

defineOptions({ name: 'GettingStartedChecklist' });

const { t } = useI18n();

const POPPER_OPTIONS = { container: '#zone-teleport', strategy: 'fixed' };
const defaultPopperOptions = Object.fromEntries(Object.keys(POPPER_OPTIONS).map((key) => [key, floatingOptions[key]]));

Object.assign(floatingOptions, POPPER_OPTIONS);
onUnmounted(() => { Object.assign(floatingOptions, defaultPopperOptions); });

const started = computed(() => !showWelcome);
const justDoneId = ref(null);
const toasts = ref([]);
const remounts = ref(0);
const reviewing = ref(false);
const setupCallPostponed = ref(false);

const indexUrl = computed(() => organization_getting_started_index_path(organization.id));
const dashboardUrl = computed(() => auto_dashboard_path(organization.id, { dashboard_nav: 1 }));
const allCompleted = computed(() => steps.every(({ status }) => status === 'finished'));

const nextOpenStep = (fromId) => {
  const index = steps.findIndex(({ id }) => id === fromId);
  const ordered = [...steps.slice(index + 1), ...steps.slice(0, Math.max(index, 0))];

  return ordered.find(({ status }) => status !== 'finished')?.id ?? null;
};

const activeStep = computed(() => {
  const current = steps.find(({ id }) => id === step);

  if (!current || stepData === null) {return null;}

  const base = { data: stepData, completed: current.status === 'finished', organization, onSaved: saved, onCancel: cancel };
  const nextStepId = nextOpenStep(step);
  const skippable = { ...base, onSkip: skip, unskippable: !nextStepId };
  const props = {
    create_org: { nextStepId, dashboardUrl: dashboardUrl.value, onStart: () => open(nextStepId) },
    set_up_project: { ...skippable, currentUserId: currentUser.id },
    create_a_smart_notification: { ...skippable, onRefresh: () => router.reload({ only: ['stepData'] }) },
    connect_payroll: base,
  }[step] ?? skippable;

  return { component: STEP_COMPONENTS[step], props };
});

const visit = (params, only, options = {}) => router.get(indexUrl.value, params, { only, preserveState: true, preserveScroll: true, ...options });

const open = (id, options = {}) => {
  if (!id) {return;}

  visit({ step: id }, ['step', 'stepData'], options);
};

const start = () => {
  visit(step ? { step } : {}, ['step', 'stepData', 'showWelcome']);
};

const review = () => {
  open(steps[0].id, { onSuccess: () => { reviewing.value = true; } });
};

const skip = () => {
  open(nextOpenStep(step));
};

const cancel = () => {
  remounts.value += 1;
};

const removeToast = (toast) => {
  toasts.value = toasts.value.filter((other) => other !== toast);
};

const saved = () => {
  const next = nextOpenStep(step);

  justDoneId.value = step;
  toasts.value = [...toasts.value, {
    title: t('step_done', { name: t(`step_names.${step}`) }),
    description: next ? t('moving_on') : undefined,
    color: 'success',
    position: 'top-right',
  }];
  visit({ step: next ?? step }, ['steps', 'step', 'stepData'], { onSuccess: () => { remounts.value += 1; } });
};
</script>

<style scoped>
:global(.content-wrapper:has(.getting-started-checklist)) { padding-bottom: 0; }
:global(.content-wrapper:has(.getting-started-checklist) .container-fluid) { padding: var(--topbar-height) 0 0; }

@keyframes step-lift { from { opacity: 0; transform: translateY(1rem) scale(0.99); } to { opacity: 1; transform: none; } }

.step-lift { animation: step-lift 320ms cubic-bezier(0.16, 1, 0.3, 1) both; }

@media (prefers-reduced-motion: reduce) {
  .step-lift { animation: none; }
}
</style>

<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "welcome": "Welcome, {name}!",
    "your_plan": "Your plan: {plan}",
    "step_done": "{name} done!",
    "moving_on": "Nice work, moving you to the next step."
  }
}
</i18n>
